import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import Moment from "moment";

import {
  idbClearOrders,
  idbDeleteOrder,
  idbGetAllOrders,
  idbPutOrder,
} from "./utils/db/indexedDbOrders";
import {
  storeEncryptedCache,
  readEncryptedCache,
} from "./utils/cache/cryptoCache";
import {
  findOrderBicci,
  findOrderBicciPending,
  listenOrdersByUpdatedAt,
} from "./utils/firebase/orders";
import {
  getAllConsoleIntegrations,
  getConsoleIntegrations,
} from "./utils/firebase/integrations";
import { cancellationStatesFalabbelaSelector } from "../../utils/objects/translateStates";
import { UserContext } from "../../context/CurrentUserProvider";
import { removeDuplicates } from "./utils/helpers/removeDuplicates";

function useGetOrdersLocalDataContextByCompany({
  initDateInitial,
  endDateInitial,
}) {
  const { userDataContext } = useContext(UserContext);

  const [shouldSubscribe, setShouldSubscribe] = useState(false);
  const [excludePendings, setExcludePendings] = useState(false);
  const listenerRef = useRef(null);

  // offsets equivalentes a tu código original
  const START_OFFSET_DAYS = 0;
  const END_OFFSET_DAYS = 0;

  const [initDate, setInitDate] = useState(
    initDateInitial || Moment().format("YYYY-MM-DD")
  );
  const [endDate, setEndDate] = useState(
    endDateInitial || Moment().format("YYYY-MM-DD")
  );

  const [mergedOrders, setMergedOrders] = useState([]);
  const [loadingContext, setLoading] = useState(false);
  const [companiesData, setCompaniesData] = useState([]);
  const [userAccess, setUserAcess] = useState([]);

  const companyIDs_Int = useMemo(() => {
    if (userDataContext && userDataContext.userData) {
      const companyCollaborator = [
        ...(userDataContext.userData.companyCollaborator || []),
      ];
      const userCompanyID = userDataContext.userData.companyID;
      if (userCompanyID && !companyCollaborator.includes(userCompanyID)) {
        companyCollaborator.push(userCompanyID);
      }
      setUserAcess(companyCollaborator);
      return companyCollaborator;
    }
    return [];
  }, [userDataContext]);

  const isAdmin = useMemo(
    () => companyIDs_Int.includes("BicciAdmin"),
    [companyIDs_Int]
  );

  function listenStop() {
    if (listenerRef.current) {
      listenerRef.current();
      listenerRef.current = null;
    }
  }

  function listenStart(arrayCompanies, isAdminFlag, initISO, endISO) {
    listenStop();
    listenerRef.current = listenOrdersByUpdatedAt(
      initISO,
      endISO,
      async (changes) => {
        const pendingToStore = [];
        const companyIDs = (arrayCompanies || []).map(
          (c) => c.integrationServicesBicciID
        );

        for (const change of changes) {
          const updated = { id: change.doc.id, ...change.doc.data() };

          if (!isAdminFlag && !companyIDs.includes(updated.companyID)) continue;
          if (updated?.lostDelivery === true) continue;
          if (cancellationStatesFalabbelaSelector.includes(updated?.intState))
            continue;

          if (change.type === "removed") {
            await idbDeleteOrder(change.doc.id);
          } else {
            pendingToStore.push(updated);
          }
        }

        const unique = removeDuplicates(pendingToStore);
        for (const o of unique) await idbPutOrder(o);

        const all = await idbGetAllOrders();
        const filtered = isAdminFlag
          ? all
          : all.filter((o) => companyIDs.includes(o.companyID));
        setMergedOrders(filtered);
      }
    );
  }

  useEffect(() => {
    const load = async () => {
      setLoading(true);

      await idbClearOrders();

      const cached = await idbGetAllOrders();
      setMergedOrders(cached);

      let ordersToday = [];
      let ordersPending = [];

      const pwd = process.env.REACT_APP_SECRETENCRIPT_WEB;

      if (isAdmin) {
        let integrations = await readEncryptedCache(pwd);
        if (!integrations || integrations.length === 0) {
          integrations = await getAllConsoleIntegrations(companyIDs_Int);
          await storeEncryptedCache(integrations, pwd);
        }
        setCompaniesData(integrations);

        ordersToday = await findOrderBicci(
          integrations,
          initDate,
          endDate,
          true,
          START_OFFSET_DAYS,
          END_OFFSET_DAYS
        );
        ordersPending = excludePendings
          ? []
          : await findOrderBicciPending(integrations, true);
      } else {
        let integrations = await readEncryptedCache(pwd);
        if (!integrations || integrations.length === 0) {
          integrations = await getConsoleIntegrations(companyIDs_Int);
          await storeEncryptedCache(integrations, pwd);
        }
        setCompaniesData(integrations);

        ordersToday = await findOrderBicci(
          integrations,
          initDate,
          endDate,
          false,
          START_OFFSET_DAYS,
          END_OFFSET_DAYS
        );
        ordersPending = excludePendings
          ? []
          : await findOrderBicciPending(integrations, false);
      }

      const all = removeDuplicates([...ordersToday, ...ordersPending]);
      for (const o of all) await idbPutOrder(o);

      const allStored = await idbGetAllOrders();
      setMergedOrders(allStored);

      if (shouldSubscribe && companyIDs_Int.length > 0) {
        listenStart(companiesData, isAdmin, initDate, endDate);
      }

      setLoading(false);
    };

    listenStop();
    load();

    return () => listenStop();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    initDate,
    endDate,
    companyIDs_Int,
    isAdmin,
    shouldSubscribe,
    excludePendings,
  ]);

  return {
    mergedOrders,
    loadingContext,
    companiesData,
    initDate,
    endDate,
    setInitDate,
    setEndDate,
    listenStart,
    listenStop,
    setShouldSubscribe,
    excludePendings,
    setExcludePendings,
  };
}

export default useGetOrdersLocalDataContextByCompany;
